<template>
  <div class="layout-spacing">
    <v-stepper :model-value="stepActive"
               flat
               alt-labels
               hide-actions
               class="bg-transparent"
    >
      <v-stepper-header>
        <template v-for="(label, index) in stepLabels" :key="index">
          <v-stepper-item :title="label" :value="index + 1"/>
          <v-divider v-if="index < stepLabels.length - 1"/>
        </template>
      </v-stepper-header>

      <AppCard ref="content"
              class="overflow-y-scroll" elevation="0"
              style="height: 75vh"
      >
        <div v-if="creationLoading" class="d-flex justify-center">
          <v-progress-circular style="width: 64px; height: 64px"
                               class="my-8"
                               indeterminate
          />
        </div>

        <v-stepper-window>
          <v-stepper-window-item :value="1">
            <ArchivesAddStep1 v-if="stepActive === 1 && !creationLoading"
                              ref="step1"
                              :importedFiles="importedFiles"
                              :archivingConfig="archivingConfig"
                              :validator="v$"
                              @archivingConfig="onUpdatedArchivingConfig"
                              @updateUploadedFiles="importedFiles = $event"
            />
          </v-stepper-window-item>

          <v-stepper-window-item :value="2">
            <ArchivesAddStep2 v-if="stepActive === 2" :importedFiles="importedFiles"/>
          </v-stepper-window-item>

          <v-stepper-window-item :value="3">
            <ArchivesAddStep3 v-if="stepActive === 3" v-model:value="importedFiles"/>
          </v-stepper-window-item>

          <v-stepper-window-item :value="4">
            <ArchivesAddStep4 v-if="stepActive === 4"
                              ref="step4"
                              :importedFiles="importedFiles"
                              @uploadStarted="onUploadStarted"
                              @uploadFinished="onUploadFinished"
            />
          </v-stepper-window-item>
        </v-stepper-window>
      </AppCard>
    </v-stepper>

    <div class="d-flex mt-2"
         :class="{
            'justify-space-between': stepActive > 1,
            'justify-end': stepActive === 1,
          }"
    >
      <app-button v-if="stepActive > 1"
        :disabled="uploadStarted || (isUploadFinished && isUploadSuccessful)"
        type="outlined"
        variant="neutral"
        @click="stepActive--"
      >
        <app-material-icon
          v-if="$vuetify.display.mdAndUp"
          name="arrow_back"
          color="inherit"
        />
        {{ $t(`common.previous`) }}
      </app-button>
      <app-button
        :disabled="uploadStarted || (isUploadFinished && isUploadSuccessful)"
        @click="checkStepValidation"
      >
        {{ stepActive === 4 ? $t(`archives.views.ArchivesAdd.buttonLastStep`) : $t(`common.next`) }}
        <app-material-icon
          v-if="stepActive < 4 && $vuetify.display.mdAndUp"
          name="arrow_forward"
          color="inherit"
        />
      </app-button>
    </div>

    <ArchivesAddWarningDialog v-if="showWarningDialog"
                              @confirm="$refs.step4.startUpload"
                              @close="showWarningDialog = false"
    />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { createNamespacedHelpers } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import { formatImportedFile } from '@/common/utils/archives'
import roomService from '@/services/room.service'
import { BiblesActionTypes } from '@/store/modules/bibles/action_types'
import { FilesActionTypes } from '@/store/modules/files/action_types'
import { GET_SIGNED_DOCUMENTS } from '@/store/modules/signed-documents/action_types'
import { useArchivesStore } from '@/stores/archivesStore'

import ArchivesAddStep1 from '../steps/ArchivesAddStep1.vue'
import ArchivesAddStep2 from '../steps/ArchivesAddStep2.vue'
import ArchivesAddStep3 from '../steps/ArchivesAddStep3.vue'
import ArchivesAddStep4 from '../steps/ArchivesAddStep4.vue'
import ArchivesAddWarningDialog from '../steps/ArchivesAddWarningDialog.vue'

const { mapActions: mapFilesActions } = createNamespacedHelpers('files')
const { mapActions: mapBiblesActions } = createNamespacedHelpers('bibles')
const { mapActions: mapSignedDocumentsActions } = createNamespacedHelpers('signedDocuments')
export default {
  name: 'ArchivesAdd',
  components: {
    AppCard,
    ArchivesAddStep1,
    ArchivesAddStep2,
    ArchivesAddStep3,
    ArchivesAddStep4,
    ArchivesAddWarningDialog,
  },
  setup() {
    const archivesStore = useArchivesStore()
    return {
      archivesStore,
      v$: useVuelidate(),
    }
  },
  validations: {
    archivingConfig: { required },
    importedFiles: { required },
  },
  data () {
    const stepLabels = [
      this.$t('archives.views.ArchivesAdd.step2Title'),
      this.$t('archives.views.ArchivesAdd.step1Title'),
      this.$t('archives.views.ArchivesAdd.step3Title'),
      this.$t('archives.views.ArchivesAdd.step4Title'),
    ]

    return {
      archivingConfig: {},
      creationLoading: false,
      importedFiles: [],
      showWarningDialog: false,
      stepActive: 1,
      stepLabels,
      uploadStarted: false,
      isUploadFinished: false,
      isUploadSuccessful: true,
    }
  },
  computed: {
    sections() {
      return this.archivesStore.sections
    },
  },
  watch: {
    stepActive () {
      this.$refs.content.$el.scrollTop = 0
    },
  },
  async created () {
    if (this.sections && this.sections.length === 1) {
      this.archivingConfig = this.sections[0]
    }
    const { query } = this.$route
    const {
      bibles: biblesFromState = [],
      files: filesFromState = [],
      signedDocuments: signedDocumentsFromState = [],
    } = window.history.state
    const filePromises = []
    const biblePromises = []
    let newFiles = filesFromState
    let newBibles = biblesFromState
    let newSignedDocuments = signedDocumentsFromState
    try {
      // If we navigated here through the application's runtime,
      // the files are passed with their information already through the router
      // Otherwise, we only have the file ids in the URL left and have to fetch
      // their information.
      // TODO: We could use a route for files and bibles would take ids as params to
      // get in only one roundtrip all the elements needed
      // This approach currently assumes we don't have a lot of elements and
      // avoids any ids that doesn't exist / errors
      this.creationLoading = true
      let room = {}
      if (query.fromMnemo) {
        const { data } =  await roomService.getRoomByMnemo(query.fromMnemo)
        room = data
      }
      if (filesFromState.length === 0 && query.fileIds) {
        const mnemo = query.fromMnemo
        const fileIdsArray = query.fileIds.split(',')
        for (const fileId of fileIdsArray) {
          filePromises.push(this.GET_FILE({
            mnemo,
            id: fileId,
          }))
        }
        const promisedFiles = await Promise.allSettled(filePromises)
        newFiles = []
        promisedFiles.forEach((promisedFile) => {
          if (promisedFile.status === 'fulfilled') {
            newFiles.push(promisedFile.value)
          }
        })
      }
      if (biblesFromState.length === 0 && query.bibleIds) {
        const mnemo = query.fromMnemo
        const bibleIdsArray = query.bibleIds.split(',')
        for (const bibleId of bibleIdsArray) {
          biblePromises.push(
            this.GET_BIBLE({
              mnemo,
              bibleId,
            }),
          )
        }
        const promisedBibles = await Promise.allSettled(biblePromises)
        newBibles = []
        promisedBibles.forEach((promisedBible) => {
          if (promisedBible.status === 'fulfilled') {
            newBibles.push(promisedBible.value.data)
          }
        })
      }

      if (signedDocumentsFromState.length === 0 && query.signedDocumentIds) {
        const mnemo = query.fromMnemo
        const signedDocumentIdsQuery = query.signedDocumentIds
        newSignedDocuments = await this.GET_SIGNED_DOCUMENTS({
          mnemo,
          params: {
            onlyIds: signedDocumentIdsQuery,
            fileDetails: true,
            rightsDetails: true,
            signatureDetails: true,
          },
        })
      }
      newFiles =
        newFiles.map((file) => formatImportedFile(file, this.archivingConfig.id))
      newBibles =
        newBibles.map((bible) => this.formatBibleToImportedFile(bible))
      // Signed documents are already handled since it's a possible input from the picker
      newSignedDocuments =
        newSignedDocuments.map((signedDocument) => formatImportedFile(signedDocument, this.archivingConfig.id))
      this.importedFiles = newFiles.concat(newBibles, newSignedDocuments)
      if (room?.cmRef) {
        this.importedFiles = this.importedFiles.map(obj => ({
           ...obj,
           objectToPost: {
          ...obj.objectToPost,
          fileRef: room.cmRef,
         },
        }))
      }
    } catch (e) {
      console.error(e)
    } finally {
      this.creationLoading = false
    }
  },
  methods: {
    ...mapFilesActions([FilesActionTypes.GET_FILE]),
    ...mapBiblesActions([BiblesActionTypes.GET_BIBLE]),
    ...mapSignedDocumentsActions([GET_SIGNED_DOCUMENTS]),
    formatBibleToImportedFile (bible) {
      return {
        objectToPost: {
          bibleId: bible.id,
          archivingConfigId: this.archivingConfig.id,
          fileRights: [],
          fileRef: '',
          fileName: bible.name,
          notify: false,
          notifyMessage: '',
          fileSigners: [],
          fileSigningDate: '',
          fileIsSigned: false,
        },
        extension: 'zip',
        size: bible.size,
        originalFile: bible,
        name: bible.name,
        signedOnClosd: false,
        status: 'pending',
      }
    },
    onUpdatedArchivingConfig (event) {
      this.v$.archivingConfig.$model = event
      this.importedFiles.forEach((file) => {
        file.objectToPost.archivingConfigId = event.id
      })
    },
    checkStepValidation () {
      if (this.stepActive === 1) {
        this.v$.$validate()
        if (!this.v$.$invalid) {
          this.stepActive++
        }
      } else if (this.stepActive === 2) {
        this.stepActive++
      } else if (this.stepActive === 3) {
        this.stepActive++
      } else if (this.stepActive === 4) {
        if (this.archivesStore.muteArchivingWarning) {
          this.$refs.step4.startUpload()
        } else {
          this.showWarningDialog = true
        }
      }
    },
    onUploadStarted () {
      // Make sure any files is now ready to be queued, even if they were errored before
      // But we shouldn't try to upload the ones that are successful again.
      this.importedFiles = this.importedFiles.filter((importedFile) => importedFile.status !== 'success').map((importedFile) => ({ ...importedFile, status: 'queued' }))
      this.isUploadFinished = false
      this.isUploadStarted = true
    },
    onUploadFinished (isUploadSuccessful) {
      this.isUploadFinished = true
      this.isUploadSuccessful = isUploadSuccessful
      this.isUploadStarted = false
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.v-stepper-header) {
  max-width: 1280px;
  box-shadow: none;
  margin-inline: auto;
}
.ArchiveAdd-title {
  font-size: 1.3rem;
  font-weight: bold;
}
.overflow-y-scroll {
  overflow-y: auto;
}
</style>
